import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { gsap } from "gsap";
import { 
  getTenantId, 
    parseArrayData,
  getDependentsWithAccess,
  updateDependent,
  deleteDependent,
  uploadImageIfNeeded,
  addDependent,
  createDependentWithAccessCode,
  getAssignedBuildings,
  normalizePhoneNumber,
} from "./TenantAuth";
import { useTenantHeader } from "./TenantLayout";
import toast from "react-hot-toast";
import { confirmDialog } from "../../components/ConfirmDialog";
import * as XLSX from "xlsx";

const PAGE_SIZE = 20;

function toTitleCase(str = "") {
  return str.toLowerCase().split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function resolveBuilding(input, buildingList = []) {
  if (!input) {
    if (buildingList.length === 1) return buildingList[0];
    return null;
  }
  const str = String(input).trim().toLowerCase();
  let found = buildingList.find(b => String(b.buildingId || b.id).toLowerCase() === str);
  if (found) return found;
  found = buildingList.find(b => String(b.number || "").trim().toLowerCase() === str);
  if (found) return found;
  const stripped = str.replace(/^(house|block|flat|unit|sq|staff\s*quarter)\s*/i, "");
  found = buildingList.find(b => {
    const bNum = String(b.number || "").trim().toLowerCase().replace(/^(house|block|flat|unit|sq|staff\s*quarter)\s*/i, "");
    return bNum === stripped || bNum === str;
  });
  if (found) return found;
  found = buildingList.find(b => {
    const phase = String(b.phaseName || "").trim().toLowerCase();
    const type = String(b.typeName || "").trim().toLowerCase();
    return (phase && str.includes(phase)) || (type && str.includes(type));
  });
  if (found) return found;
  if (buildingList.length === 1) return buildingList[0];
  return null;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getInitials(first = "", last = "") {
  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (clean.slice(0, 2) || "??").toUpperCase();
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Avatar({ src, name, size = 48, onClick }) {
  const initials = getInitials(name || "");
  return (
    <span
      className={`d6m-avatar ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      onClick={onClick}
      title={onClick ? "View photo" : undefined}
    >
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay d6-topnav-preloader ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const BLANK_FORM = {
  title: "",
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  designation: "Family Member",
  buildingId: "",
  isPermanent: true,
  imageUrl: "",
  notes: "",
  hasAccessCode: true,
  passDuration: "",
};

// ─── Modal for Detailed Info & QR Code ───────────────────────────────────────
function ResidentDetailModal({ modalRef, resident, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const handleClose = () => { hide(); onClose?.(); };

  if (!resident) {
    return (
      <ModalShell
        modalRef={modalRef}
        icon="ki-security-user"
        title="Dependant Pass & Details"
        onClose={handleClose}
        width={760}
      >
        <div className="d6m-empty">No resident selected.</div>
      </ModalShell>
    );
  }

  const isPermanent = resident.isPermanent;
  const identifier = resident.code || resident.accessCode || resident.phoneNumber || "N/A";
  const fullName = [resident.title, resident.firstName, resident.lastName].filter(Boolean).join(" ") || resident.name || "Dependant";
  const bldgLabel = resident.currentBuildingNumber
    ? `${resident.currentPhaseName || ""} — ${String(resident.currentBuildingNumber).startsWith("Staff Quarter") ? resident.currentBuildingNumber : `House ${resident.currentBuildingNumber}`}`
    : (resident.buildingName || resident.phaseName || "Assigned Building");

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=600,height=600');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Access QR Pass - ${fullName}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; text-align: center; padding: 40px; color: #333; }
            .card { border: 2px dashed #0EA84A; border-radius: 16px; padding: 30px; max-width: 400px; margin: 0 auto; background: #fff; }
            h2 { margin: 0 0 5px 0; color: #111; }
            .house-tag { color: #0EA84A; font-weight: bold; margin-bottom: 20px; font-size: 15px; }
            .qr-container { display: inline-block; padding: 15px; border: 1px solid #eaeaea; border-radius: 12px; margin-bottom: 15px; background: #f9f9f9; }
            img { width: 220px; height: 220px; }
            .meta { font-size: 13px; color: #666; margin-top: 10px; line-height: 1.4; }
          </style>
        </head>
        <body>
          <div class="card">
            <h2>${fullName}</h2>
            <div class="house-tag">${bldgLabel}</div>
            <div class="qr-container">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(identifier)}" alt="QR Code" />
            </div>
            <div class="meta">
              <strong>Access Passcode / ID:</strong> ${identifier}<br/>
              <strong>Access Type:</strong> ${isPermanent ? "Permanent Resident" : "Temporary Pass"}<br/>
              <strong>Valid Until:</strong> ${resident.expiresAt ? fmtDate(resident.expiresAt) : (isPermanent ? "Unlimited" : "Active")}
            </div>
          </div>
          <script>
            window.onload = () => { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleShareWhatsApp = () => {
    const qrUrl = resident.qrUrl || `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(identifier)}`;
    const text = `Hello ${fullName},\n\nHere is your estate access pass for ${bldgLabel}:\n• Access Passcode: ${identifier}\n• Type: ${isPermanent ? "Permanent Resident" : "Temporary Pass"}\n• QR Code: ${qrUrl}`;

    const cleanPhone = (resident.phoneNumber || "").replace(/[^0-9]/g, "");
    const waUrl = cleanPhone
      ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;

    window.open(waUrl, "_blank");
  };

  const handleShareEmail = () => {
    const qrUrl = resident.qrUrl || `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(identifier)}`;
    const subject = `Access Pass & QR Code — ${bldgLabel}`;
    const body = `Dear ${fullName},\n\nHere are your access pass details for ${bldgLabel}:\n\n- Resident: ${fullName}\n- Designation: ${resident.designation || "Family Member"}\n- Residence: ${bldgLabel}\n- Access Passcode: ${identifier}\n- Validity: ${isPermanent ? "Permanent Resident" : (resident.expiresAt ? fmtDate(resident.expiresAt) : "Active")}\n- QR Code Image: ${qrUrl}\n\nBest regards,\nEstate Management`;

    const mailtoUrl = `mailto:${resident.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-security-user"
      title="Dependant Pass & Details"
      onClose={handleClose}
      width={760}
      footer={
        <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <button type="button" className="btn btn-light d6-mbtn" onClick={handlePrint}>
              <i className="ki-outline ki-printer fs-5 me-2.5 text-gray-700"></i>
              Print Tag
            </button>
            <button type="button" className="btn btn-light-success d6-mbtn" onClick={handleShareWhatsApp}>
              <i className="ki-outline ki-whatsapp fs-5 me-2.5 text-success"></i>
              WhatsApp
            </button>
            <button type="button" className="btn btn-light-info d6-mbtn" onClick={handleShareEmail}>
              <i className="ki-outline ki-sms fs-5 me-2.5 text-info"></i>
              Email
            </button>
          </div>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>
            Close
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-6">
        {/* Details */}
        <div className="w-100">
          <div className="d6m-person mb-5">
            <Avatar src={resident.imageUrl} name={fullName} size={54} />
            <div className="min-w-0 flex-grow-1">
              <div className="d6m-name fs-5 text-truncate">{fullName}</div>
              <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                {resident.designation && (
                  <span className="badge bg-light-success text-success fs-9 fw-bold rounded-pill px-2.5 py-0.5">
                    {resident.designation}
                  </span>
                )}
                <StatusChip color={isPermanent ? "#0EA84A" : "#D97706"}>
                  {isPermanent ? "Permanent Resident" : "Temporary Pass"}
                </StatusChip>
              </div>
            </div>
          </div>

          <div className="d6m-section mt-0">
            <div className="d6m-section-head">
              <span>Contact &amp; Residence Details</span>
            </div>
            <div className="d6m-kv d6m-kv-4">
              <div className="d6m-kv-cell">
                <span className="d6m-k">Residence / Building</span>
                <span className="d6m-v text-truncate d-block">{bldgLabel}</span>
              </div>
              <div className="d6m-kv-cell">
                <span className="d6m-k">Phone Number</span>
                <span className="d6m-v text-truncate d-block">{resident.phoneNumber || "—"}</span>
              </div>
              <div className="d6m-kv-cell">
                <span className="d6m-k">Email Address</span>
                <span className="d6m-v text-truncate d-block">{resident.email || "—"}</span>
              </div>
              <div className="d6m-kv-cell">
                <span className="d6m-k">Access Status</span>
                <span className="d6m-v text-success fw-bold">Active Authorized</span>
              </div>
            </div>
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head">
              <span>Access Timeline</span>
            </div>
            <div className="d6m-kv">
              <div className="d6m-kv-cell">
                <span className="d6m-k">Access Issued</span>
                <span className="d6m-v">{fmtDate(resident.createdAt || resident.issuedDate || new Date())}</span>
              </div>
              <div className="d6m-kv-cell">
                <span className="d6m-k">Validity Period</span>
                <span className={`d6m-v fw-bold ${isPermanent ? "text-success" : "text-warning"}`}>
                  {isPermanent ? "Unlimited (Permanent)" : (resident.expiresAt ? `Expires ${fmtDate(resident.expiresAt)}` : "Active Temporary")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: QR Pass Card */}
        <div className="w-100 d-flex flex-column align-items-center justify-content-center text-center">
          <div className="d6-card p-5 w-100 bg-light d-flex flex-column align-items-center border border-gray-200">
            <span className="d6m-muted fs-8 fw-semibold mb-3">Estate QR Pass</span>
            <div className="bg-white p-3 rounded-3 shadow-xs border border-gray-200 mb-3">
              {resident.qrUrl || resident.qrCode ? (
                <img 
                  src={resident.qrUrl || `data:image/png;base64,${resident.qrCode}`} 
                  alt="QR Code" 
                  style={{ width: 160, height: 160, objectFit: "contain" }} 
                />
              ) : (
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(identifier)}`} 
                  alt="QR Code" 
                  style={{ width: 160, height: 160, objectFit: "contain" }} 
                />
              )}
            </div>

            <div className="d6m-k text-uppercase fs-9 tracking-wider fw-bold">Secure Access Passcode</div>
            <div className="d6-num text-success my-1 letter-spacing-2" style={{ letterSpacing: "2px" }}>
              {identifier}
            </div>
            <span className="d6m-hint fs-9">Scan at estate gate scanner for verified entry</span>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// ─── Add / Edit modal ─────────────────────────────────────────
function DependantModal({ modalRef, mode, initial, buildings, onSave, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const [form, setForm]     = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const imgRef = useRef(null);

  useEffect(() => {
    setForm(initial || BLANK_FORM);
    setErrors({});
  }, [initial]);

  const handleClose = () => {
    hide();
    setErrors({});
    onClose?.();
  };

  const field = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file size should be under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(p => ({ ...p, imageUrl: ev.target.result }));
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const validate = () => {
    const e = {};
    if (!form.firstName?.trim()) e.firstName = "First name is required";
    if (!form.lastName?.trim())  e.lastName  = "Last name is required";
    if (!form.phoneNumber?.trim()) e.phoneNumber = "Phone number is required";
    if (mode !== "edit" && form.hasAccessCode && !form.buildingId) {
      e.buildingId = "Select the building this pass is for";
    }
    if (mode !== "edit" && form.hasAccessCode && !form.isPermanent && !form.passDuration) {
      e.passDuration = "Expiration date is required for temporary passes";
    }
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Please fill all required fields.");
    }
    return Object.keys(e).length === 0;
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);
      handleClose();
    } finally {
      setSaving(false);
    }
  };

  const calcDaysBadge = (passDateStr) => {
    if (!passDateStr) return "1 day";
    const exp = new Date(passDateStr);
    const now = new Date();
    const diffDays = Math.max(1, Math.round((exp - now) / (1000 * 60 * 60 * 24)));
    return `${diffDays} day${diffDays !== 1 ? "s" : ""}`;
  };

  const isPresetSelected = (passDateStr, days) => {
    if (!passDateStr) return false;
    const exp = new Date(passDateStr);
    const now = new Date();
    const diffDays = Math.max(1, Math.round((exp - now) / (1000 * 60 * 60 * 24)));
    return diffDays === days;
  };

  const ROLE_OPTIONS = [
    { value: "Family Member", label: "Family Member" },
    { value: "Spouse", label: "Spouse" },
    { value: "Child", label: "Child" },
    { value: "Domestic Staff", label: "Domestic Staff" },
    { value: "Driver", label: "Driver" },
    { value: "Cook", label: "Cook" },
    { value: "Nanny", label: "Nanny" },
    { value: "Visitor", label: "Visitor" },
    { value: "Delivery Person", label: "Delivery Person" },
    { value: "Service Person", label: "Service Person (Artisan / Tech)" },
  ];

  const QUICK_PERSON_TYPES = [
    { label: "Family Member", icon: "ki-people" },
    { label: "Staff", icon: "ki-briefcase" },
    { label: "Visitor", icon: "ki-user" },
    { label: "Delivery Person", icon: "ki-delivery-time" },
    { label: "Service Person", icon: "ki-wrench" },
  ];

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-people"
      title={mode === "edit" ? "Edit Dependant & Access" : "Add Dependant / Access"}
      onClose={handleClose}
      closeDisabled={saving}
      width={720}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : (
              mode === "edit" ? "Save Changes" : "Add Dependant"
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSave} className="d-flex flex-column gap-6">
        {/* Photo */}
        <div className="w-100">
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="d6m-name fs-8">Dependant Photo</span>
              {form.imageUrl && (
                <button
                  type="button"
                  className="btn btn-link btn-color-danger p-0 fs-9 fw-semibold text-decoration-none"
                  onClick={() => setForm(p => ({ ...p, imageUrl: "" }))}
                >
                  Remove Photo
                </button>
              )}
            </div>

            <input
              ref={imgRef}
              accept="image/*"
              className="d-none"
              type="file"
              onChange={handleImageFile}
            />

            {form.imageUrl ? (
              <div className="d6-card p-4 d-flex flex-column align-items-center justify-content-center bg-light border border-gray-200 mb-4">
                <Avatar src={form.imageUrl} name={form.firstName} size={84} />
                <button
                  type="button"
                  className="btn btn-sm btn-light d6-mbtn mt-3"
                  onClick={() => imgRef.current?.click()}
                >
                  <i className="ki-outline ki-camera fs-6 me-2.5" /> Change Photo
                </button>
              </div>
            ) : (
              <div
                className="d6-card p-4 d-flex flex-column align-items-center justify-content-center border border-dashed border-gray-300 bg-hover-light cursor-pointer mb-4"
                style={{ minHeight: "130px" }}
                onClick={() => imgRef.current?.click()}
              >
                <div className="symbol symbol-45px symbol-circle bg-light-success text-success d-flex align-items-center justify-content-center mb-2">
                  <i className="ki-outline ki-camera fs-3 text-success" />
                </div>
                <span className="fs-8 fw-semibold text-gray-800">Click to upload snapshot</span>
                <span className="fs-9 text-gray-400 mt-0.5">PNG, JPG up to 5MB</span>
              </div>
            )}

          </div>

        </div>

        {/* Details */}
        <div className="w-100">
          <div className="row g-3">
            <Field className="col-md-3" label="Title">
              <select className="d6m-input" value={form.title} onChange={field("title")}>
                {["", "Mr", "Mrs", "Miss", "Dr", "Prof", "Engr"].map(t => (
                  <option key={t} value={t}>{t || "Select…"}</option>
                ))}
              </select>
            </Field>

            <Field className="col-md-4" label="First Name" required error={errors.firstName}>
              <input
                className="d6m-input"
                placeholder="Enter first name"
                value={form.firstName}
                onChange={field("firstName")}
                required
              />
            </Field>

            <Field className="col-md-5" label="Last Name" required error={errors.lastName}>
              <input
                className="d6m-input"
                placeholder="Enter last name"
                value={form.lastName}
                onChange={field("lastName")}
                required
              />
            </Field>

            <Field className="col-md-6" label="Phone Number" required error={errors.phoneNumber}>
              <input
                type="tel"
                className="d6m-input"
                placeholder="e.g. 08012345678"
                value={form.phoneNumber}
                onChange={field("phoneNumber")}
                required
              />
            </Field>

            <Field className="col-md-6" label="Email Address">
              <input
                type="email"
                className="d6m-input"
                placeholder="email@example.com"
                value={form.email}
                onChange={field("email")}
              />
            </Field>

            {/* Person Type */}
            <div className="col-12">
              <label className="d-block fs-8 fw-semibold text-gray-700 mb-2">Person Type</label>
              <div className="row g-2">
                {QUICK_PERSON_TYPES.map(pt => {
                  const isMatch = form.designation?.toLowerCase()?.includes(pt.label.toLowerCase().split(" ")[0]);
                  return (
                    <div key={pt.label} className="col-6 col-sm-4">
                      <button
                        type="button"
                        className={`btn w-100 py-2.5 px-3 fs-8 rounded-3 d-flex align-items-center justify-content-start gap-2 transition-all ${
                          isMatch ? "btn-success" : "btn-light text-gray-700 border border-gray-200"
                        }`}
                        onClick={() => setForm(p => ({ ...p, designation: pt.label }))}
                      >
                        <i className={`ki-outline ${pt.icon} fs-5 flex-shrink-0 ${isMatch ? "text-white" : "text-gray-500"}`} />
                        <span className="text-start">{pt.label}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <Field className="col-12" label="Relationship / Role">
              <select className="d6m-input" value={form.designation} onChange={field("designation")}>
                {ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>

            {/* Notes */}
            <Field className="col-12" label="Notes (Optional)">
              <input
                className="d6m-input"
                placeholder="e.g. Sister visiting for the weekend, Private driver"
                value={form.notes || ""}
                onChange={field("notes")}
              />
            </Field>

            {/* Access Pass */}
            {mode !== "edit" && (
              <div className="col-12 mt-2">
                <div className="d6-card p-3.5 border border-success border-opacity-30 bg-light">
                  <div className="form-check form-switch form-check-custom d-flex align-items-center">
                    <input
                      className="form-check-input h-20px w-32px cursor-pointer"
                      id="generateCodeToggle"
                      type="checkbox"
                      checked={form.hasAccessCode}
                      onChange={e => setForm(p => ({ ...p, hasAccessCode: e.target.checked }))}
                    />
                    <label className="form-check-label ms-3 cursor-pointer" htmlFor="generateCodeToggle">
                      <span className="d6m-name fs-8 d-block">Assign access pass immediately</span>
                      <span className="d6m-muted fs-9">Generates numerical passcode &amp; QR credential upon save</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {mode !== "edit" && form.hasAccessCode && (
              <>
                <Field className="col-12" label="Assign to Building" required error={errors.buildingId}>
                  <select className="d6m-input" value={form.buildingId} onChange={field("buildingId")}>
                    <option value="">Select building…</option>
                    {buildings.map(b => {
                      const bId = b.buildingId || b.id;
                      const bLabel = [
                        b.phaseName,
                        b.number ? (String(b.number).startsWith("Staff Quarter") || String(b.number).startsWith("Block") || String(b.number).startsWith("House") ? b.number : `House ${b.number}`) : (b.buildingName || b.name),
                        b.typeName ? `(${b.typeName})` : ""
                      ].filter(Boolean).join(" — ");
                      return (
                        <option key={bId} value={bId}>{bLabel || "Building"}</option>
                      );
                    })}
                  </select>
                </Field>

                <div className="col-12">
                  <label className="d-block fs-8 fw-semibold text-gray-700 mb-2">Pass Type</label>
                  <div className="d6m-seg">
                    <button
                      type="button"
                      className={form.isPermanent ? "active" : ""}
                      onClick={() => setForm(p => ({ ...p, isPermanent: true }))}
                    >
                      <i className="ki-outline ki-shield-tick text-success" />
                      Permanent
                    </button>
                    <button
                      type="button"
                      className={!form.isPermanent ? "active" : ""}
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() + 1);
                        setForm(p => ({
                          ...p,
                          isPermanent: false,
                          passDuration: p.passDuration || d.toISOString().split("T")[0],
                        }));
                      }}
                    >
                      <i className="ki-outline ki-key text-warning" />
                      Temporary
                    </button>
                  </div>
                </div>

                <div className="col-12">
            {/* Temporary Pass Duration */}
            {!form.isPermanent ? (
              <div className="d6-card p-4 border border-gray-200 bg-white">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="fs-8 fw-bold text-gray-900">Pass Duration</span>
                  <StatusChip color="#0EA84A">
                    {calcDaysBadge(form.passDuration)}
                  </StatusChip>
                </div>

                <div className="d-flex flex-wrap gap-1.5 mb-3">
                  {[
                    { label: "1 Day", days: 1 },
                    { label: "2 Days", days: 2 },
                    { label: "3 Days", days: 3 },
                    { label: "7 Days", days: 7 },
                    { label: "30 Days", days: 30 },
                  ].map(preset => (
                    <button
                      key={preset.days}
                      type="button"
                      className={`btn btn-sm py-1 px-2.5 fs-9 rounded-pill transition-all ${
                        isPresetSelected(form.passDuration, preset.days) ? "btn-success" : "btn-light text-gray-700 border border-gray-200"
                      }`}
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() + preset.days);
                        setForm(p => ({ ...p, passDuration: d.toISOString().split("T")[0] }));
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <Field label="Expiration Date" required error={errors.passDuration}>
                  <input
                    type="date"
                    className="d6m-input"
                    min={new Date().toISOString().split("T")[0]}
                    value={form.passDuration || ""}
                    onChange={field("passDuration")}
                  />
                </Field>
              </div>
            ) : (
              <div className="d6-card p-4 border border-success border-dashed bg-light-success bg-opacity-20 text-center">
                <i className="ki-outline ki-shield-tick fs-2x text-success d-block mb-1" />
                <span className="d6m-name fs-8 text-gray-900 d-block">Permanent Resident Pass</span>
                <span className="d6m-muted fs-9 d-block mt-0.5">
                  Permanent occupants have unlimited gate access with continuous QR credentials.
                </span>
              </div>
            )}
                </div>
              </>
            )}
          </div>
        </div>

      </form>
    </ModalShell>
  );
}

// ─── Excel preview modal ─────────────────────────────────────
function ExcelImportModal({ modalRef, rows, buildings, onImport, onClose, importing, importProgress }) {
  const { hide } = useBootstrapModal(modalRef);
  const handleClose = () => { hide(); onClose?.(); };

  const resolvedRows = useMemo(() => {
    return rows.map((r, i) => {
      const resolved = resolveBuilding(r.buildingNumber, buildings);
      const normPhone = normalizePhoneNumber(r.phoneNumber);
      const isValid = Boolean(r.firstName && r.lastName && normPhone);
      return {
        ...r,
        index: i + 1,
        resolvedBuilding: resolved,
        normalizedPhone: normPhone,
        isValid,
      };
    });
  }, [rows, buildings]);

  const validCount = resolvedRows.filter(r => r.isValid).length;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-up"
      title={`Excel Import Preview — ${rows.length} Dependant${rows.length !== 1 ? "s" : ""}`}
      onClose={handleClose}
      closeDisabled={importing}
      width={720}
      subheader={
        <div className="px-6 py-2 bg-light border-bottom border-gray-100 d-flex align-items-center justify-content-between">
          <span className="fs-8 text-gray-600">
            {validCount} of {rows.length} records ready for import
          </span>
          <StatusChip color={validCount === rows.length ? "#0EA84A" : "#D97706"}>
            {validCount === rows.length ? "All Valid" : "Review Needed"}
          </StatusChip>
        </div>
      }
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={importing}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success d6-mbtn"
            onClick={onImport}
            disabled={importing || validCount === 0}
          >
            {importing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Importing…
              </>
            ) : (
              <>
                <i className="ki-outline ki-file-up fs-5 me-2.5 text-white" />
                Import {validCount} Dependant{validCount !== 1 ? "s" : ""}
              </>
            )}
          </button>
        </>
      }
    >
      <div className="table-responsive" style={{ maxHeight: "50vh" }}>
        <table className="table table-row-dashed table-row-gray-200 align-middle gs-3 gy-2.5">
          <thead>
            <tr className="fw-bold text-gray-500 fs-9 border-0 text-uppercase">
              <th>#</th>
              <th>Full Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Designation</th>
              <th>Building</th>
              <th>Access Type</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {resolvedRows.map((r, i) => (
              <tr key={i}>
                <td className="text-gray-400 fs-9">{r.index}</td>
                <td className="text-gray-900 fw-semibold fs-8">
                  {r.firstName} {r.lastName}
                </td>
                <td className="text-gray-600 fs-9 font-monospace">{r.normalizedPhone || r.phoneNumber || "—"}</td>
                <td className="text-gray-500 fs-9">{r.email || "—"}</td>
                <td className="text-gray-700 fs-9">
                  <span className="badge bg-light text-gray-800 fw-semibold rounded-pill px-2 py-0.5">{r.designation || "Family"}</span>
                </td>
                <td>
                  {r.resolvedBuilding ? (
                    <span className="badge bg-light-success text-success fs-9 fw-bold rounded-pill px-2 py-0.5">
                      {r.resolvedBuilding.phaseName ? `${r.resolvedBuilding.phaseName} — ` : ""}
                      {String(r.resolvedBuilding.number).startsWith("Staff Quarter") ? r.resolvedBuilding.number : `House ${r.resolvedBuilding.number}`}
                    </span>
                  ) : r.buildingNumber ? (
                    <span className="badge bg-light-warning text-warning fs-9 rounded-pill px-2 py-0.5" title="Building not matched">
                      {r.buildingNumber}
                    </span>
                  ) : (
                    <span className="badge bg-light text-gray-500 fs-9 rounded-pill px-2 py-0.5">General</span>
                  )}
                </td>
                <td>
                  <StatusChip color={r.isPermanent ? "#0EA84A" : "#D97706"}>
                    {r.isPermanent ? "Permanent" : `${r.durationValue || 30}d`}
                  </StatusChip>
                </td>
                <td>
                  {r.isValid ? (
                    <span className="badge bg-light-success text-success fs-9 fw-bold rounded-pill px-2 py-0.5 d-inline-flex align-items-center gap-1">
                      <i className="ki-outline ki-check-circle text-success fs-7" /> Ready
                    </span>
                  ) : (
                    <span className="badge bg-light-danger text-danger fs-9 fw-bold rounded-pill px-2 py-0.5 d-inline-flex align-items-center gap-1">
                      <i className="ki-outline ki-cross-circle text-danger fs-7" /> Incomplete
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {importing && (
        <div className="pt-3">
          <div className="progress h-6px rounded-pill">
            <div
              className="progress-bar bg-success"
              style={{ width: `${importProgress}%`, transition: "width .3s" }}
            />
          </div>
          <div className="text-gray-500 fs-9 text-center mt-1.5">{importProgress}% imported</div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── Main page ────────────────────────────────────────────────
export default function TenantDependants() {
  const dashboardRef = useRef(null);
  const tenantId   = getTenantId();
  const [loading,  setLoading]  = useState(true);
  const [deps,     setDeps]     = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [search,   setSearch]   = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [filterDesignation, setFilterDesignation] = useState("");
  const [filterBuilding, setFilterBuilding] = useState("");
  const [viewMode,   setViewMode]   = useState("list");
  const [page,     setPage]     = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [editTarget, setEditTarget] = useState(null);
  const [selectedResident, setSelectedResident] = useState(null);

  const [xlsxRows,     setXlsxRows]     = useState([]);
  const [importing,    setImporting]    = useState(false);
  const [importPct,    setImportPct]    = useState(0);
  const [importResult, setImportResult] = useState(null);

  const fileRef = useRef(null);

  // Modal Refs
  const residentModalRef = useRef(null);
  const dependantFormModalRef = useRef(null);
  const excelModalRef = useRef(null);
  const residentModal = useBootstrapModal(residentModalRef);
  const dependantFormModal = useBootstrapModal(dependantFormModalRef);
  const excelModal = useBootstrapModal(excelModalRef);


  // Fetch buildings for assign dropdown and matching
  useEffect(() => {
    if (!tenantId) return;
    getAssignedBuildings({ pageIndex: 1, pageSize: 100, id: tenantId })
      .then(res => {
        const list = parseArrayData(res);
        setBuildings(list);
      })
      .catch(err => {
        console.warn("Failed to load assigned buildings:", err);
      });
  }, [tenantId]);

  const fetchDeps = useCallback(async (pg = 0) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getDependentsWithAccess({
        pageIndex: pg,
        pageSize: PAGE_SIZE,
        search,
        id: tenantId,
      });
      const list = parseArrayData(data);
      setDeps(list);
      setTotalCount(data?.totalCount || list.length);
      setPage(pg);
    } catch { 
      toast.error("Failed to load dependants."); 
    } finally { 
      setLoading(false); 
    }
  }, [tenantId, search]);

  useEffect(() => { fetchDeps(0); }, [fetchDeps]);

  useEffect(() => {
    if (loading) return undefined;
    const context = gsap.context(() => {
      gsap.fromTo(".gsap-hero", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.06, duration: 0.55, ease: "power3.out", delay: 0.05, clearProps: "all" });
      gsap.fromTo(".gsap-card", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.08, duration: 0.55, ease: "power2.out", delay: 0.2, clearProps: "all" });
    }, dashboardRef);
    return () => context.revert();
  }, [loading]);

  // ─── Save (add/edit) ─────────────────────────────────────────
  const handleSave = async (form) => {
    try {
      const imageUrl = await uploadImageIfNeeded(form.imageUrl, tenantId);
      const notes = form.notes?.trim() || form.designation?.trim() || "";
      if (editTarget?.id || editTarget?.dependentId) {
        await updateDependent(editTarget.dependentId || editTarget.id, {
          firstName: toTitleCase(form.firstName),
          lastName:  toTitleCase(form.lastName),
          phoneNumber: form.phoneNumber.trim(),
          email:       form.email,
          imageUrl,
          dependentType: form.designation,
          notes,
        }, tenantId);
        toast.success("Dependant updated.");
      } else if (form.hasAccessCode && form.buildingId) {
        let durationValue = 30;
        let durationUnit = 2; // Days
        if (!form.isPermanent && form.passDuration) {
          const exp = new Date(form.passDuration);
          const now = new Date();
          const diffDays = Math.max(1, Math.ceil((exp - now) / (1000 * 60 * 60 * 24)));
          durationValue = diffDays;
          durationUnit = 2;
        }
        await createDependentWithAccessCode({
          firstName: toTitleCase(form.firstName),
          lastName: toTitleCase(form.lastName),
          phoneNumber: form.phoneNumber.trim(),
          email: form.email,
          imageUrl,
          notes,
          dependentType: form.designation,
          isAccessCodePermanent: Boolean(form.isPermanent),
          durationValue,
          durationUnit,
          buildingId: form.buildingId,
        }, tenantId);
        toast.success("Dependant & Access Pass created.");
      } else {
        await addDependent({
          firstName: toTitleCase(form.firstName),
          lastName: toTitleCase(form.lastName),
          phoneNumber: form.phoneNumber.trim(),
          email: form.email?.trim() || "",
          imageUrl,
          dependentType: form.designation,
          notes,
        }, tenantId);
        toast.success("Dependant created.");
      }
      setEditTarget(null);
      fetchDeps(page);
    } catch (err) {
      toast.error(err.message || "Operation failed.");
      throw err;
    }
  };

  // ─── Delete ──────────────────────────────────────────────────
  const handleDelete = async (dep) => {
    if (!(await confirmDialog({ title: "Remove Dependant", message: `Remove ${dep.firstName} ${dep.lastName}? Their access will stop working.`, confirmLabel: "Remove" }))) return;
    try {
      await deleteDependent(dep.dependentId || dep.id, tenantId);
      toast.success("Dependant removed.");
      fetchDeps(page);
    } catch (err) { toast.error(err.message); }
  };

  // ─── Excel parse ─────────────────────────────────────────────
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        if (!rows || rows.length === 0) {
          toast.error("The selected Excel file is empty.");
          return;
        }
        const mapped = rows.map(r => {
          const fn = String(r.firstName || r["First Name"] || r["first_name"] || r["Firstname"] || r["firstname"] || "").trim();
          const ln = String(r.lastName || r["Last Name"] || r["last_name"] || r["Lastname"] || r["lastname"] || "").trim();
          const fullName = String(r.name || r["Full Name"] || r["Name"] || "").trim();
          const [splitFn, ...splitLnParts] = fullName.split(" ");

          const rawPhone = String(r.phoneNumber || r["Phone Number"] || r.phone || r["Phone"] || r["Phone No"] || r["Mobile"] || r["mobile"] || "").trim();
          const rawEmail = String(r.email || r["Email"] || r["Email Address"] || "").trim();
          const rawDesignation = String(r.designation || r["Designation"] || r.type || r["Type"] || r.role || r["Role"] || r.relationship || r["Relationship"] || "").trim();
          const rawBuilding = String(r.buildingNumber || r["Building Number"] || r.building || r["Building"] || r["House"] || r["House Number"] || r["Flat"] || r["Unit"] || "").trim();
          const rawPermanent = String(r.isPermanent || r["Permanent"] || r["Is Permanent"] || r["Is Permanent (yes/no)"] || r["Pass Type"] || "");
          const rawDuration = Number(r.durationValue || r["Duration"] || r["Duration (Days)"] || r["Days"] || 30);
          const rawNotes = String(r.notes || r["Notes"] || r.note || r["Note"] || "").trim();

          return {
            title: String(r.title || r["Title"] || "").trim(),
            firstName: toTitleCase(fn || splitFn || ""),
            lastName: toTitleCase(ln || splitLnParts.join(" ") || ""),
            phoneNumber: normalizePhoneNumber(rawPhone) || rawPhone,
            email: rawEmail,
            designation: rawDesignation || "Family",
            buildingNumber: rawBuilding,
            isPermanent: /^(yes|true|1|perm|permanent)$/i.test(rawPermanent.trim()),
            durationValue: !isNaN(rawDuration) && rawDuration > 0 ? rawDuration : 30,
            notes: rawNotes,
          };
        });
        setXlsxRows(mapped);
        excelModal.show();
      } catch (err) {
        toast.error("Could not parse Excel file: " + err.message);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  // ─── Bulk import ─────────────────────────────────────────────
  const handleImport = async () => {
    if (!xlsxRows || xlsxRows.length === 0) return;
    setImporting(true);
    setImportPct(0);
    setImportResult(null);

    const results = { ok: 0, fail: 0, errors: [] };

    for (let i = 0; i < xlsxRows.length; i++) {
      const r = xlsxRows[i];
      if (!r.firstName || !r.lastName || !r.phoneNumber) {
        results.fail++;
        results.errors.push(`Row ${i + 1} (${r.firstName || "Unknown"}): Missing name or phone number.`);
        setImportPct(Math.round(((i + 1) / xlsxRows.length) * 100));
        continue;
      }

      const bld = resolveBuilding(r.buildingNumber, buildings);
      const bId = bld?.buildingId || bld?.id;

      try {
        if (bId) {
          await createDependentWithAccessCode({
            firstName: r.firstName,
            lastName: r.lastName,
            phoneNumber: r.phoneNumber,
            dependentType: r.designation,
            isAccessCodePermanent: Boolean(r.isPermanent),
            durationValue: r.durationValue || (r.isPermanent ? 365 : 30),
            durationUnit: 2, // 2 = Days
            buildingId: bId,
          }, tenantId);
        } else {
          await addDependent({
            firstName: r.firstName,
            lastName: r.lastName,
            phoneNumber: r.phoneNumber,
            email: r.email || "",
            dependentType: r.designation,
            notes: r.notes || r.designation || "",
          }, tenantId);
        }
        results.ok++;
      } catch (err) {
        results.fail++;
        results.errors.push(`Row ${i + 1} (${r.firstName} ${r.lastName}): ${err.message}`);
      }
      setImportPct(Math.round(((i + 1) / xlsxRows.length) * 100));
    }

    setImporting(false);
    setImportResult(results);
    excelModal.hide();
    fetchDeps(0);

    if (results.fail === 0) {
      toast.success(`Successfully imported all ${results.ok} dependant${results.ok !== 1 ? "s" : ""}!`);
    } else if (results.ok > 0) {
      toast(`${results.ok} imported, ${results.fail} failed. Check errors for details.`, { icon: "⚠️" });
    } else {
      toast.error(`Import failed: ${results.errors[0] || ""}`);
    }
  };

  const handleDownloadTemplate = useCallback(() => {
    const sampleBld = buildings[0]?.number || "Block A";
    const wsData = [
      ["First Name", "Last Name", "Phone Number", "Email", "Designation", "Building Number", "Is Permanent (yes/no)", "Duration (Days)", "Notes"],
      ["Ada", "Okafor", "+2348012345678", "ada.okafor@example.com", "Family", sampleBld, "yes", 365, "Spouse"],
      ["Emeka", "Chukwu", "+2348098765432", "emeka@example.com", "Staff", sampleBld, "yes", 180, "Driver"],
      ["Sarah", "Williams", "+2348123456789", "sarah.w@example.com", "Visitor", sampleBld, "no", 7, "Guest"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 26 },
      { wch: 16 },
      { wch: 18 },
      { wch: 24 },
      { wch: 16 },
      { wch: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dependants_Template");
    XLSX.writeFile(wb, "Dependants_Import_Template.xlsx");
  }, [buildings]);

  const { setHeaderConfig } = useTenantHeader();

  useEffect(() => {
    setHeaderConfig({
      title: "Dependant Management",
      breadcrumb: "Dependant Management",
      actions: null,
    });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null });
  }, [setHeaderConfig]);

  const openResident = (d) => {
    setSelectedResident(d);
    residentModal.show();
  };

  const openAdd = () => {
    setEditTarget(null);
    dependantFormModal.show();
  };

  const openEdit = (d) => {
    setEditTarget(d);
    dependantFormModal.show();
  };

  const designations = useMemo(() => {
    const set = new Set();
    deps.forEach(d => { if (d.designation) set.add(d.designation); });
    return Array.from(set);
  }, [deps]);

  const filteredDeps = useMemo(() => {
    return deps.filter(d => {
      if (typeFilter === "permanent" && !d.isPermanent) return false;
      if (typeFilter === "temporary" && d.isPermanent) return false;
      if (filterDesignation && d.designation !== filterDesignation) return false;
      if (filterBuilding && String(d.buildingId || d.currentBuildingNumber) !== String(filterBuilding)) return false;
      return true;
    });
  }, [deps, typeFilter, filterDesignation, filterBuilding]);

  return (
    <>
      <PagePreloader visible={loading} />
      <input type="file" ref={fileRef} accept=".xlsx,.xls,.csv" className="d-none" onChange={handleFile} />

      <div ref={dashboardRef} className="app-container container-fluid py-6 px-6 px-lg-10">

        {/* Green Top Stats Section */}
        <div className="d6-kpi-brand p-6 mb-5">
          <div className="row g-4 g-xl-0 align-items-center">
            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-people fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Total Dependants</div>
                    <div className="d6-num text-white my-0.5">{totalCount}</div>
                    <div className="text-white text-opacity-70 fs-9">Family &amp; staff members</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-shield-tick fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Permanent Access</div>
                    <div className="d6-num text-white my-0.5">{deps.filter(d => d.isPermanent).length}</div>
                    <div className="text-white text-opacity-70 fs-9">Resident credentials</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-time fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Temporary Access</div>
                    <div className="d6-num text-white my-0.5">{deps.filter(d => !d.isPermanent).length}</div>
                    <div className="text-white text-opacity-70 fs-9">Time-limited visitor passes</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-home-2 fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Linked Buildings</div>
                    <div className="d6-num text-white my-0.5">{buildings.length || 1}</div>
                    <div className="text-white text-opacity-70 fs-9">Assigned properties</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="d6-card p-6 mb-5 gsap-card">
          <CardHead
            title="All Registered Dependants"
            subtitle="Search, filter, and manage household members & visitor access passes"
            className="mb-5"
          >
            <div className="position-relative">
              <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
              <input
                type="text"
                className="d6m-input ps-10"
                style={{ width: 220, minHeight: 40, height: 40, borderRadius: 999 }}
                placeholder="Search name, house, phone..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {designations.length > 0 && (
              <select
                className="d6m-input"
                style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
                value={filterDesignation}
                onChange={e => setFilterDesignation(e.target.value)}
              >
                <option value="">All Roles</option>
                {designations.map(des => (
                  <option key={des} value={des}>{des}</option>
                ))}
              </select>
            )}

            <select
              className="d6m-input"
              style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              <option value="all">All Access Types</option>
              <option value="permanent">Permanent</option>
              <option value="temporary">Temporary</option>
            </select>

            {buildings.length > 0 && (
              <select
                className="d6m-input"
                style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
                value={filterBuilding}
                onChange={e => setFilterBuilding(e.target.value)}
              >
                <option value="">All Buildings</option>
                {buildings.map(b => {
                  const id = b.buildingId || b.id;
                  const label = b.number 
                    ? (String(b.number).startsWith("Staff Quarter") || String(b.number).startsWith("Block") || String(b.number).startsWith("House") ? b.number : `House ${b.number}`)
                    : (b.buildingName || b.name || "Building");
                  return (
                    <option key={id} value={id}>{label}</option>
                  );
                })}
              </select>
            )}

            <div className="d6-seg">
              <button
                type="button"
                className={`fs-8 fw-bold ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                title="List view"
              >
                <i className="ki-outline ki-row-horizontal fs-5 m-0"></i>
              </button>
              <button
                type="button"
                className={`fs-8 fw-bold ${viewMode === "card" ? "active" : ""}`}
                onClick={() => setViewMode("card")}
                title="Grid view"
              >
                <i className="ki-outline ki-element-11 fs-5 m-0"></i>
              </button>
            </div>

            <button
              type="button"
              className="btn btn-light-primary d6-mbtn"
              onClick={() => fileRef.current?.click()}
            >
              <i className="ki-outline ki-file-up fs-5 me-2.5" />
              Upload Excel
            </button>
            <button
              type="button"
              className="btn btn-light d6-mbtn"
              onClick={handleDownloadTemplate}
            >
              <i className="ki-outline ki-file-down fs-5 me-2.5" />
              Template
            </button>
            <button
              type="button"
              className="btn btn-success d6-mbtn"
              onClick={openAdd}
            >
              <i className="ki-outline ki-plus fs-5 me-2 text-white" />
              Add Dependant
            </button>
          </CardHead>

          <div>
            {importResult && (
              <div className={`alert alert-${importResult.fail === 0 ? "success" : "warning"} d-flex align-items-center p-4 mb-5 rounded-3`}>
                <i className={`ki-outline ki-${importResult.fail === 0 ? "check-circle" : "warning"} fs-2 me-3 text-${importResult.fail === 0 ? "success" : "warning"}`} />
                <div className="fs-8">
                  <strong>{importResult.ok} imported successfully.</strong>
                  {importResult.fail > 0 && <span className="text-danger ms-1">({importResult.fail} failed: {importResult.errors.slice(0, 2).join("; ")})</span>}
                </div>
                <button type="button" className="btn btn-sm btn-light ms-auto d6-mbtn py-1 px-3" onClick={() => setImportResult(null)}>
                  Dismiss
                </button>
              </div>
            )}

            {filteredDeps.length === 0 && !loading ? (
              <div className="d6m-empty py-12">
                <i className="ki-outline ki-people fs-3x text-gray-300 d-block mb-3"></i>
                <div className="d6-title text-gray-900 mb-1">No dependants found</div>
                <div className="fs-8 text-gray-500">Try adjusting your search query or filter options.</div>
              </div>
            ) : viewMode === "list" ? (
              <div className="d-flex flex-column gap-3">
                {filteredDeps.map(d => {
                  const fullName = [d.title, d.firstName, d.lastName].filter(Boolean).join(" ") || "Dependant";
                  const passCode = d.code || d.accessCode || "Passcode";
                  return (
                    <div
                      key={d.dependentId || d.id}
                      className="d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 border-hover-success position-relative overflow-hidden cursor-pointer transition-all"
                      style={{ minHeight: 90 }}
                      onClick={() => openResident(d)}
                    >
                      <div className={`position-absolute top-0 start-0 h-100 ${d.isPermanent ? "bg-success" : "bg-warning"} w-4px`}></div>
                      
                      <div className="d-flex align-items-center me-4" style={{ width: "380px", flexShrink: 0 }}>
                        <Avatar src={d.imageUrl} name={fullName} size={48} />
                        <div className="d-flex flex-column overflow-hidden ms-3 min-w-0">
                          <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                            <span className="fs-6 fw-bold text-gray-900 text-truncate">{fullName}</span>
                            {d.designation && (
                              <span className="badge bg-light-success text-success fs-9 fw-semibold rounded-pill px-2.5 py-0.5">
                                {d.designation}
                              </span>
                            )}
                          </div>
                          <div className="text-gray-500 fs-8 text-truncate">{d.phoneNumber || d.email || "No contact info"}</div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0 flex-wrap">
                        <div className="d-none d-sm-block text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Residence</div>
                          <div className="fw-semibold text-gray-900 fs-8 text-truncate" style={{ maxWidth: 160 }}>
                            {d.currentBuildingNumber ? `${d.currentPhaseName || ""} — ${String(d.currentBuildingNumber).startsWith("Staff Quarter") ? d.currentBuildingNumber : `House ${d.currentBuildingNumber}`}` : "—"}
                          </div>
                        </div>
                        <div className="d-none d-sm-block text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Access Pass</div>
                          <span 
                            className="badge bg-light-success text-success fw-bold fs-8 px-3 py-1 rounded-pill cursor-pointer d-inline-flex align-items-center"
                            onClick={(e) => { e.stopPropagation(); openResident(d); }}
                            title="Click to view QR code and pass details"
                          >
                            <i className="ki-outline ki-scan-barcode fs-6 text-success me-1"></i>
                            {passCode}
                          </span>
                        </div>
                        <div className="d-none d-sm-block text-center">
                          <div className="text-gray-400 fs-9 mb-0.5">Access Type</div>
                          <StatusChip color={d.isPermanent ? "#0EA84A" : "#D97706"}>
                            {d.isPermanent ? "Permanent" : "Temporary"}
                          </StatusChip>
                        </div>
                        <div className="d-flex align-items-center gap-2 ms-2">
                          <button
                            type="button"
                            className="d6-icon-btn"
                            title="View QR Pass"
                            onClick={(e) => { e.stopPropagation(); openResident(d); }}
                          >
                            <i className="ki-outline ki-scan-barcode fs-5 text-success" />
                          </button>
                          <button
                            type="button"
                            className="d6-icon-btn"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); openEdit(d); }}
                          >
                            <i className="ki-outline ki-pencil fs-5 text-gray-700" />
                          </button>
                          <button
                            type="button"
                            className="d6-icon-btn"
                            title="Remove"
                            onClick={(e) => { e.stopPropagation(); handleDelete(d); }}
                          >
                            <i className="ki-outline ki-trash fs-5 text-danger" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="row g-5">
                {filteredDeps.map(d => {
                  const fullName = [d.title, d.firstName, d.lastName].filter(Boolean).join(" ") || "Dependant";
                  const passCode = d.code || d.accessCode || "Passcode";
                  return (
                    <div className="col-12 col-sm-6 col-xl-4" key={d.dependentId || d.id}>
                      <div 
                        className="d6-card h-100 d-flex flex-column cursor-pointer border-hover-success transition-all p-5 position-relative overflow-hidden"
                        onClick={() => openResident(d)}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <div className="d-flex align-items-center gap-3 min-w-0">
                            <Avatar src={d.imageUrl} name={fullName} size={44} />
                            <div className="min-w-0">
                              <div className="fs-6 fw-bold text-gray-900 text-truncate">{fullName}</div>
                              <div className="text-gray-500 fs-8 text-truncate">{d.phoneNumber || d.email || "No contact"}</div>
                            </div>
                          </div>
                          <StatusChip color={d.isPermanent ? "#0EA84A" : "#D97706"}>
                            {d.isPermanent ? "Permanent" : "Temporary"}
                          </StatusChip>
                        </div>

                        <div className="p-3 bg-light rounded-3 mb-4">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="min-w-0">
                              <div className="text-gray-400 fs-9 mb-0.5">Residence</div>
                              <div className="fs-8 fw-semibold text-gray-800 text-truncate">
                                {d.currentBuildingNumber
                                  ? `${d.currentPhaseName || ""} — ${String(d.currentBuildingNumber).startsWith("Staff Quarter") ? d.currentBuildingNumber : `House ${d.currentBuildingNumber}`}`
                                  : "Assigned Residence"}
                              </div>
                            </div>
                            {d.designation && (
                              <span className="badge bg-light-success text-success fs-9 fw-semibold rounded-pill px-2.5 py-0.5">
                                {d.designation}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between p-3 border border-success border-opacity-25 rounded-3 bg-light-success bg-opacity-20 mb-4">
                          <div className="d-flex align-items-center gap-2">
                            <i className="ki-outline ki-scan-barcode fs-4 text-success"></i>
                            <div className="d-flex flex-column">
                              <span className="fs-9 text-gray-500">Access Passcode</span>
                              <span className="fs-7 fw-bold text-success letter-spacing-1">{passCode}</span>
                            </div>
                          </div>
                          <button 
                            type="button"
                            className="btn btn-sm btn-success d6-mbtn py-1 px-3 fs-9"
                            onClick={(e) => { e.stopPropagation(); openResident(d); }}
                          >
                            View Pass
                          </button>
                        </div>

                        <div className="d-flex align-items-center justify-content-end gap-2 mt-auto pt-3 border-top border-gray-100">
                          <button 
                            type="button"
                            className="d6-icon-btn"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); openEdit(d); }}
                          >
                            <i className="ki-outline ki-pencil fs-5 text-gray-700"></i>
                          </button>
                          <button 
                            type="button"
                            className="d6-icon-btn"
                            title="Remove"
                            onClick={(e) => { e.stopPropagation(); handleDelete(d); }}
                          >
                            <i className="ki-outline ki-trash fs-5 text-danger"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            <div className="d-flex align-items-center justify-content-between mt-6 pt-4 border-top border-gray-100 flex-wrap gap-3">
              <div className="text-gray-500 fs-8">
                Showing {totalCount > 0 ? page * PAGE_SIZE + 1 : 0}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount} dependants
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light d6-mbtn px-3"
                  style={{ height: 32 }}
                  disabled={page === 0}
                  onClick={() => fetchDeps(page - 1)}
                >
                  <i className="ki-outline ki-left fs-6 m-0"></i>
                </button>
                <span className="btn btn-sm btn-success d6-mbtn px-3 py-0" style={{ height: 32 }}>
                  {page + 1}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-light d6-mbtn px-3"
                  style={{ height: 32 }}
                  disabled={(page + 1) * PAGE_SIZE >= totalCount}
                  onClick={() => fetchDeps(page + 1)}
                >
                  <i className="ki-outline ki-right fs-6 m-0"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code / Resident Detail Modal */}
      <ResidentDetailModal
        modalRef={residentModalRef}
        resident={selectedResident}
        onClose={() => setSelectedResident(null)}
      />

      {/* Add / Edit modal */}
      <DependantModal
        modalRef={dependantFormModalRef}
        mode={editTarget ? "edit" : "add"}
        initial={editTarget ? {
          title: editTarget.title || "",
          firstName: editTarget.firstName || "",
          lastName: editTarget.lastName || "",
          email: editTarget.email || "",
          phoneNumber: editTarget.phoneNumber || "",
          designation: editTarget.designation || "Family Member",
          buildingId: editTarget.currentBuildingId || editTarget.buildingId || "",
          isPermanent: editTarget.isPermanent ?? true,
          imageUrl: editTarget.imageUrl || "",
          notes: editTarget.notes || "",
          hasAccessCode: true,
          passDuration: editTarget.expiresAt ? editTarget.expiresAt.split("T")[0] : "",
        } : BLANK_FORM}
        buildings={buildings}
        onSave={handleSave}
        onClose={() => setEditTarget(null)}
      />

      {/* Excel import preview */}
      <ExcelImportModal
        modalRef={excelModalRef}
        rows={xlsxRows}
        buildings={buildings}
        onImport={handleImport}
        onClose={() => setXlsxRows([])}
        importing={importing}
        importProgress={importPct}
      />
    </>
  );
}
